// Conversation history store — persists a list of named conversations
// to localStorage and lazily syncs from GET /api/conversations when the
// endpoint exists. Falls back silently if it doesn't.
//
// Schema (localStorage key `vella_conversations`):
//   [{ id, title, preview, updated_at, messages: [...] }]
// Active conversation id is stored in `vella_active_conversation`.
//
// Migration: on first read, if a legacy `vella_chat_session` flat array
// exists, it's wrapped into a single conversation so the user doesn't
// lose their current chat.

const LIST_KEY = "vella_conversations";
const ACTIVE_KEY = "vella_active_conversation";
const PROJECTS_KEY = "vella_projects";
const LEGACY_KEY = "vella_chat_session";
const MAX_PER_CONVO = 50;
const MAX_CONVOS = 30;

let _cache = null;
let _projectsCache = null;
const _listeners = new Set();

function uid() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function safeRead(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeWrite(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (err) {
    // Most likely cause is quota-exceeded (Safari private mode caps at
    // ~5 MB). Log so support can spot it; we don't surface to the user
    // because the in-memory cache still works for the current session.
    console.warn(`[conversationStore] ${key} write failed:`, err?.message || err);
  }
}

function deriveTitle(messages) {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser) return "New conversation";
  const t = (firstUser.content || "").trim().replace(/\s+/g, " ");
  if (!t) return "New conversation";
  return t.length > 48 ? `${t.slice(0, 46)}…` : t;
}

function derivePreview(messages) {
  const last = messages[messages.length - 1];
  if (!last) return "";
  const t = (last.content || "").trim().replace(/\s+/g, " ");
  return t.length > 80 ? `${t.slice(0, 78)}…` : t;
}

function load() {
  if (_cache) return _cache;
  let list = safeRead(LIST_KEY);
  if (!Array.isArray(list)) list = [];

  // One-time migration from the flat `vella_chat_session`.
  if (list.length === 0) {
    const legacy = safeRead(LEGACY_KEY);
    if (Array.isArray(legacy) && legacy.length > 0) {
      const id = uid();
      list = [
        {
          id,
          title: deriveTitle(legacy),
          preview: derivePreview(legacy),
          updated_at: Date.now(),
          messages: legacy.slice(-MAX_PER_CONVO),
        },
      ];
      safeWrite(LIST_KEY, list);
      safeWrite(ACTIVE_KEY, id);
    }
  }

  _cache = list;
  return list;
}

function persist(list) {
  _cache = list;
  safeWrite(LIST_KEY, list.slice(0, MAX_CONVOS));
  for (const fn of _listeners) {
    try { fn(); } catch {}
  }
}

export function listConversations() {
  return load().slice().sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
}

export function getActiveId() {
  let id = null;
  try { id = localStorage.getItem(ACTIVE_KEY); } catch {}
  const list = load();
  if (id && list.find((c) => c.id === id)) return id;
  return list[0]?.id || null;
}

export function setActiveId(id) {
  try { localStorage.setItem(ACTIVE_KEY, id); } catch {}
  for (const fn of _listeners) {
    try { fn(); } catch {}
  }
}

export function getConversation(id) {
  return load().find((c) => c.id === id) || null;
}

export function newConversation(projectId = null) {
  const list = load().slice();
  const id = uid();
  list.unshift({
    id,
    title: "New conversation",
    preview: "",
    updated_at: Date.now(),
    project_id: projectId || null,
    messages: [],
  });
  persist(list);
  setActiveId(id);
  return id;
}

export function updateConversationMessages(id, messages) {
  if (!id) return;
  const list = load().slice();
  const idx = list.findIndex((c) => c.id === id);
  const trimmed = messages.slice(-MAX_PER_CONVO);
  const next = {
    id,
    messages: trimmed,
    title: deriveTitle(trimmed) || "New conversation",
    preview: derivePreview(trimmed),
    updated_at: Date.now(),
  };
  if (idx === -1) list.unshift(next);
  else list[idx] = { ...list[idx], ...next };
  persist(list);
}

export function deleteConversation(id) {
  const list = load().filter((c) => c.id !== id);
  persist(list);
  if (getActiveId() === id) {
    const remaining = list[0]?.id || null;
    if (remaining) setActiveId(remaining);
    else try { localStorage.removeItem(ACTIVE_KEY); } catch {}
  }
}

export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function notify() {
  for (const fn of _listeners) {
    try { fn(); } catch {}
  }
}

// ─── Projects ──────────────────────────────────────────────────────────
// Projects group related conversations. Stored locally under
// `vella_projects` as [{ id, name, description, created_at }]. A
// conversation references its project via `project_id` (null = ungrouped).

function loadProjects() {
  if (_projectsCache) return _projectsCache;
  let list = safeRead(PROJECTS_KEY);
  if (!Array.isArray(list)) list = [];
  _projectsCache = list;
  return list;
}

function persistProjects(list) {
  _projectsCache = list;
  safeWrite(PROJECTS_KEY, list);
  notify();
}

export function listProjects() {
  return loadProjects().slice().sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
}

export function createProject(name, description = "") {
  const clean = (name || "").trim();
  if (!clean) return null;
  const list = loadProjects().slice();
  const id = `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  list.unshift({ id, name: clean.slice(0, 60), description: (description || "").trim().slice(0, 200), created_at: Date.now() });
  persistProjects(list);
  return id;
}

export function updateProject(id, updates = {}) {
  const list = loadProjects().slice();
  const idx = list.findIndex((p) => p.id === id);
  if (idx === -1) return;
  const next = { ...list[idx] };
  if (typeof updates.name === "string") next.name = updates.name.trim().slice(0, 60);
  if (typeof updates.description === "string") next.description = updates.description.trim().slice(0, 200);
  list[idx] = next;
  persistProjects(list);
}

// Delete a project. Conversations belonging to it are kept but
// un-grouped (project_id reset to null) so history is never lost.
export function deleteProject(id) {
  const list = loadProjects().filter((p) => p.id !== id);
  persistProjects(list);
  const convos = load().slice();
  let changed = false;
  for (let i = 0; i < convos.length; i++) {
    if (convos[i].project_id === id) {
      convos[i] = { ...convos[i], project_id: null };
      changed = true;
    }
  }
  if (changed) persist(convos);
}

export function assignConversation(convoId, projectId) {
  const list = load().slice();
  const idx = list.findIndex((c) => c.id === convoId);
  if (idx === -1) return;
  list[idx] = { ...list[idx], project_id: projectId || null };
  persist(list);
}

// Remove every conversation (history) while preserving projects.
export function clearAllConversations() {
  persist([]);
  try { localStorage.removeItem(ACTIVE_KEY); } catch {}
  notify();
}

/**
 * Try to fetch /api/conversations and merge server-side conversations
 * into the local cache. Backend may not exist yet — failures are silent.
 * Returns true if a remote sync happened, false otherwise.
 */
export async function syncFromServer(api) {
  if (!api?.getConversations) return false;
  try {
    const data = await api.getConversations();
    const remote = Array.isArray(data) ? data : data?.conversations;
    if (!Array.isArray(remote)) return false;

    const local = load();
    const byId = new Map(local.map((c) => [c.id, c]));
    for (const r of remote) {
      if (!r?.id) continue;
      const existing = byId.get(r.id);
      const merged = {
        id: r.id,
        title: r.title || existing?.title || "Untitled",
        preview: r.preview || existing?.preview || "",
        updated_at: r.updated_at || existing?.updated_at || Date.now(),
        messages: existing?.messages || r.messages || [],
      };
      byId.set(r.id, merged);
    }
    persist(Array.from(byId.values()));
    return true;
  } catch {
    return false;
  }
}
